import { QueryClient, UseMutationResult } from '@tanstack/react-query'
import {
  TRPCClientError,
  TRPCClientErrorLike,
  createTRPCClient,
  httpBatchLink,
  httpSubscriptionLink,
  loggerLink,
  retryLink,
  splitLink,
} from '@trpc/client'
import { createTRPCContext, createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import pluralize from 'pluralize'
import { getConfigValue } from '~/helpers/envHelpers'
import { isJSONString } from '~/helpers/jsonHelpers'
import {
  getAuthToken,
  getOrganizationAliasFromUrl,
  getOrganizationIdFallbackFromUrl,
} from '~/helpers/sessionHelpers'
import { logger } from '~/services/logger'

import type {
  AppRouter,
  RouterOutput as AppRouterOutput,
  RouterInput,
} from '../../../../packages/trpc/client-types'

interface DefaultErrorData {
  code: string
  httpStatus: number
  path: string[]
  message: string
  type: string
}

const isTRPCValidationError = (messages: unknown): messages is DefaultErrorData[] =>
  Array.isArray(messages)

export class TRPCValidationError {
  messages: Record<string, string> = { global: 'unknown error' }

  constructor(err: unknown) {
    this.getFieldLevelMessages = this.getFieldLevelMessages.bind(this)

    try {
      if (err instanceof TRPCClientError) {
        const messages = isJSONString(err.message) ? JSON.parse(err.message) : err.message
        if (isTRPCValidationError(messages)) {
          this.messages = messages.reduce(
            (acc, error) => ({
              ...acc,
              [error.path.join('.')]: error.message,
            }),
            {},
          )
        } else if (typeof messages === 'string') {
          this.messages = { global: messages }
        }
      }
    } catch (e) {
      logger.error(`TRPCValidationError error 002: ${err}`)
    }
  }

  public getFieldLevelMessages() {
    return this.messages
  }

  public getFriendlyMessage() {
    const numberOfErrors = Object.keys(this.messages).length
    return numberOfErrors > 1
      ? `${numberOfErrors} ${pluralize('error', numberOfErrors)} found`
      : Object.values(this.messages)[0]
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on client errors (4xx) - matching tRPC retryLink behavior
        if (error instanceof TRPCClientError) {
          const errorCode = (error as any).data?.code
          if (errorCode && errorCode !== 'INTERNAL_SERVER_ERROR') {
            return false
          }
        }
        // Retry up to 3 times for server errors (5xx)
        return failureCount < 3
      },
    },
  },
})

function getOrgHeaders() {
  return {
    'happitu-organization-alias': getOrganizationAliasFromUrl(),
    'happitu-organization-id': getOrganizationIdFallbackFromUrl(),
  }
}

function headers() {
  const token = getAuthToken()
  const headers: Record<string, string> = {
    ...getOrgHeaders(),
  }
  if (token) headers['authorization'] = `Bearer ${token}`
  return headers
}

function subscriptionUrl() {
  const base = getConfigValue('VITE_TRPC_URL')
  const url = new URL(base)
  const token = getAuthToken()
  const orgHeaders = getOrgHeaders()

  if (token) url.searchParams.set('authorization', `Bearer ${token}`)
  url.searchParams.set('happitu-organization-alias', orgHeaders['happitu-organization-alias'])
  url.searchParams.set('happitu-organization-id', orgHeaders['happitu-organization-id'])

  return url.toString()
}

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    loggerLink({
      enabled(opts) {
        return opts.direction === 'down' && opts.result instanceof Error
      },
    }),
    retryLink({
      retry(opts) {
        if (opts.error.data && opts.error.data.code !== 'INTERNAL_SERVER_ERROR') {
          // Don't retry on non-500s
          return false
        }
        if (opts.op.type !== 'query') {
          // Only retry queries
          return false
        }
        // Retry up to 3 times
        return opts.attempts < 3
      },
      // Double every attempt, with max of 30 seconds (starting at 1 second)
      retryDelayMs: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    }),
    splitLink({
      condition: (op) => op.type === 'subscription',
      true: httpSubscriptionLink({
        url: subscriptionUrl(),
      }),
      false: httpBatchLink({
        url: getConfigValue('VITE_TRPC_URL'),
        headers,
        methodOverride: 'POST',
      }),
    }),
  ],
})
export const trpcProxy = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
})

// Helper type to extract nested values via dot notation (e.g., 'queries.upsert')
type DeepValue<T, K extends string> = K extends `${infer First}.${infer Rest}`
  ? First extends keyof T
    ? DeepValue<T[First], Rest>
    : never
  : K extends keyof T
    ? T[K]
    : never
// Main utility - use like: MutationOf<'queries.upsert'>
export type MutationOf<TPath extends string> = UseMutationResult<
  DeepValue<RouterOutput, TPath>,
  TRPCClientErrorLike<AppRouter>,
  DeepValue<RouterInput, TPath>,
  unknown
>

export type RouterOutput = AppRouterOutput
export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>()
